import { waitFor } from './waitFor.js';
/**
 * Creates an exponential backoff controller for retry logic.
 *
 * The delay is calculated as: `min(baseDelay * power^retries, maxDelay)` with ±10% jitter.
 * @util
 *
 * @example
 * ```typescript
 * const backoff = createExponentialBackoff({ maxRetries: 5 });
 *
 * while (backoff.canRetry()) {
 *   try {
 *     await connect();
 *     backoff.reset();
 *     break;
 *   } catch (err) {
 *     console.log(`Retry ${backoff.retries}/${5} failed`);
 *     await backoff.backoff(); // Wait before next retry
 *   }
 * }
 * ```
 *
 * @param options - Configuration options
 * @returns Exponential backoff controller
 */
export function createExponentialBackoff(options) {
    const maxRetries = options?.maxRetries ?? Infinity;
    const baseDelay = options?.baseDelay ?? 1000;
    const power = options?.power ?? 2;
    const maxDelay = options?.maxDelay ?? 30000;
    let retries = 0;
    return {
        get retries() {
            return retries;
        },
        canRetry() {
            return retries < maxRetries;
        },
        async backoff() {
            const delay = Math.min(baseDelay * Math.pow(power, retries), maxDelay);
            // Add ±10% jitter to prevent thundering herd
            const jitter = delay * 0.1 * (Math.random() * 2 - 1);
            const actualDelay = Math.round(delay + jitter);
            retries++;
            await waitFor(actualDelay);
        },
        reset() {
            retries = 0;
        },
    };
}
//# sourceMappingURL=createExponentialBackoff.js.map